import React, { createContext, useContext } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { ME } from '../../../../libs/api/queries';
import { MeQuery, MeQueryVariables } from '../../../../libs/api/graphqlTypes';
import { AgreementSteps } from '../../../../modules/pages/createAgreement/ds/repositories/commands';
import { GqlApi, GQLApi } from '../../../../libs/api/gqlApi';
import { ErrorType, getGraphqlError } from '../../../../libs/utils/getGraphqlError';
import { BASE_URL, NEED_AUTH_ERROR } from '../../../../libs/utils/staticData';
import { Restrictions } from '../../../../modules/pages/setting/ui/context';
import useNotification from "../../../../modules/widgets/notifier/ui/hooks/useNitification";

type UserTypes = 'client' | 'admin' | 'user' | 'kias';

type AuthRedirectCmd = {
	error: ErrorType;
	needAuthCallback?: (error: string) => void;
	justErrorCallback?: (error: string) => void;
};

type GlobalContext = {
	api: GQLApi;
	user: {
		id: number | undefined;
		isSuper: boolean;
		isAdmin: boolean;
		type: UserTypes | undefined;
		restrictions: Restrictions;
		username: string | undefined;
		phone: string | undefined;
		email: string | undefined;
		roleId: number | undefined;
		groupId: number | undefined;
		curator: {
			email: string | undefined;
			phone: string | undefined;
			username: string | undefined;
		};
		banksOk: boolean;
		isStaffMember: boolean;
		isUnderAdmin: boolean;
		crossEnabled: boolean;
		flatEnabled: boolean;
	};
	routes: {
		// main lk
		authorizationDefault: () => string;
		authorizationClient: () => string;
		dashboard: () => string;
		permissionsManage: () => string;
		agreementByIdSeller: (id: string) => string;
		agreementByIdUnderwriter: (id: string) => string;
		contractById: (id: string) => string;
		flatContractById: (id: string) => string;
		crossContractById: (id: string) => string;
		contractAnketaById: (id: string) => string;
		invoiceById: (id: string) => string;
		createAgreementStep: (id: string, step: AgreementSteps) => string;
		agreementsLog: () => string;
		underwriterLog: () => string;
		contractsLog: () => string;
		prolongationLog: () => string;
		profile: () => string;
		roles: () => string;
		roleById: (id: string) => string;
        declarationById: (id: string) => string;
		settings: () => string;
		reports: () => string;
		precalculation: () => string;
		flat: () => string;
		error500: (error: ErrorType) => void;
		checkError500Handler: (cmd: AuthRedirectCmd) => void;
		redirectToAuthorization: () => void;

		// client
		policies: () => string;
	};
	mockData: boolean;
	lastError?: ErrorType;
};

const GlobalContext = createContext<GlobalContext>({} as GlobalContext);
export const useGlobalContext = (): GlobalContext => useContext(GlobalContext);

const checkError500Handler = ({ error, justErrorCallback, needAuthCallback }: AuthRedirectCmd): void => {
	const errorText = getGraphqlError(error);
	if (errorText === NEED_AUTH_ERROR) {
		const beforeAuthPage = window.location.pathname.replace(BASE_URL || '', '');
		if(beforeAuthPage && beforeAuthPage !== '/authorization'){
			localStorage.setItem('beforeAuthPage', beforeAuthPage);
			console.log('Save beforeAuthPage: ', beforeAuthPage);
		}
		if (needAuthCallback) needAuthCallback(errorText);
		return;
	}
	if (justErrorCallback) justErrorCallback(errorText);
};

export const GlobalContextProvider: React.FC = ({ children }) => {
	const navigate = useNavigate();
	const api = GqlApi();

	const [lastErrorState, setLastErrorState] = React.useState<ErrorType | null>(null);
	const { data: dataUser, loading: userLoading } = useQuery<MeQuery, MeQueryVariables>(ME, {
		onError: error => checkError500Handler({ error, justErrorCallback: () => {
			setLastErrorState(error);
			navigate('/error500');
			}, needAuthCallback: () => navigate('/authorization') }),
	});

	const redirectToAuthorization = React.useCallback(() => {
		if (dataUser?.me?.user?.type === 'client') return navigate('/authorization/client');
		return navigate('/authorization');
	}, [dataUser?.me?.user?.type, navigate]);

	const mockData: boolean = !!process.env.REACT_APP_MOCK_DATA;

	const value: GlobalContext = React.useMemo(
		() => ({
			api,
			user: {
				id: Number(dataUser?.me?.user?.id) || undefined,
				isSuper: !!dataUser?.me?.user?.isSuper,
				isAdmin: !!dataUser?.me?.user?.isAdmin,
				restrictions: (dataUser?.me?.user?.restrictions || []).reduce<Restrictions>((prev, restriction) => ({ ...prev, [restriction?.tag || 'unknown']: restriction?.value }), {}),
				username: (dataUser?.me?.user?.username || '') + (dataUser?.me?.adminUser?.username ? ` (${dataUser?.me?.adminUser?.username})` : ''),
				phone: dataUser?.me?.user?.phone || undefined,
				email: dataUser?.me?.user?.email || undefined,
				roleId: dataUser?.me?.user?.role?.id || undefined,
				groupId: dataUser?.me?.user?.group?.id || undefined,
				type: dataUser?.me?.user?.type as UserTypes,
				curator: {
					username: dataUser?.me?.user?.group?.curator?.username,
					email: dataUser?.me?.user?.group?.curator?.email || undefined,
					phone: dataUser?.me?.user?.group?.curator?.phone || undefined,
				},
				banksOk: userLoading ? true : dataUser?.me?.user?.banksOk || false,
				isStaffMember: dataUser?.me?.user?.isStaffMember || false,
				isUnderAdmin: !!dataUser?.me?.adminUser,
				crossEnabled: dataUser?.me?.user?.crossEnabled || false,
				flatEnabled: dataUser?.me?.user?.flatEnabled || false,
			},
			routes: {
				redirectToAuthorization,
				authorizationDefault: () => '/authorization',
				authorizationClient: () => '/authorization/client',
				dashboard: () => '/',
				policies: () => '/policies',
				permissionsManage: () => '/permissions',
				agreementsLog: () => '/agreementsLog',
				underwriterLog: () => '/underwriterLog',
				agreementByIdSeller: id => `/agreement/${id}`,
				agreementByIdUnderwriter: id => `/underwriting/${id}`,
				contractById: id => `/contract/${id}`,
				flatContractById: id => `/flat-contract/${id}`,
				crossContractById: id => `/cross-contract/${id}`,
				contractAnketaById: id => `/contract/${id}/anketa`,
				invoiceById: id => `/createInvoice/${id}`,
				createAgreementStep: (id, step) => `/createAgreement/${id}/${step}`,
				contractsLog: () => '/contractsLog',
				prolongationLog: () => '/prolongationLog',
				profile: () => '/profile',
				roles: () => '/roles',
				precalculation: () => '/precalculation',
				flat: () => '/flat',
				roleById: id => `/roles/${id}`,
                declarationById: id => `/declaration/${id}`,
				error500: error => checkError500Handler({
					error,
					justErrorCallback: (error) => {
						console.log('error', error);
					},
					needAuthCallback: () => redirectToAuthorization()
				}),
				settings: () => '/settings',
				reports: () => '/reports',
				checkError500Handler,
			},
			mockData,
			lastError: lastErrorState,
		}),
		[api, dataUser, redirectToAuthorization, navigate, mockData, lastErrorState]
	);

	return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
};
